import { useQuery } from "@tanstack/react-query";
import { useBrandManager } from "@/contexts/BrandProvider";
import { supabase } from "@/integrations/supabase/client";
import { type BrandFeatureKey } from "@/constants/brandFeatures";

interface CustomBrandFeature {
  id: string;
  brand_id: string;
  feature_key: string;
  enabled: boolean;
  auto_detected: boolean;
  created_at: string;
  updated_at: string;
}

export function useCustomBrandFeatures() {
  const { currentBrandId } = useBrandManager();

  // Fetch custom features for the current brand
  const { data: features = [], isLoading } = useQuery({
    queryKey: ['custom-brand-features', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];
      
      const { data, error } = await supabase
        .from('custom_brand_features')
        .select('*')
        .eq('brand_id', currentBrandId)
        .eq('enabled', true); // Only fetch enabled features

      if (error) throw error;
      return data as CustomBrandFeature[];
    },
    enabled: !!currentBrandId,
  });

  // Helper function to check if a feature is enabled
  // Features are only available if they exist in the custom_brand_features table
  const hasFeature = (featureKey: BrandFeatureKey): boolean => {
    return features.some(f => f.feature_key === featureKey && f.enabled);
  };

  return {
    features,
    hasFeature,
    isLoading,
  };
}